import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { isFulfilled, isRejected } from '@reduxjs/toolkit'
import { FC } from 'react'
import { v4 } from 'uuid'
import { FaButton } from 'web/components/generic/FaButton'
import { Header } from 'web/components/Header'
import { getCurrentBuild } from 'web/data/core-data'
import { cloneFight, cloneSetup } from 'web/data/data-cloner'
import { createExportString } from 'web/data/setup-import-export'
import { generateSetupExportLines } from 'web/data/setup-note-exporter'
import { generateWaArNote } from 'web/data/wa-ar-note-exporter'
import { useCatchError } from 'web/hooks/useCatchError'
import { useShiftKey } from 'web/hooks/useShiftKey'
import { AppActions, RenderMode, useAppState } from 'web/redux/slices/app-slice'
import { DisplayFightActions, useDisplayState } from 'web/redux/slices/display-fight-slice'
import { EditFightActions } from 'web/redux/slices/edit-fight-slice'
import { EditSetupActions } from 'web/redux/slices/edit-setup-slice'
import { FightActions, useFightSelectors, useFightState } from 'web/redux/slices/fight-slice'
import { ModalActions } from 'web/redux/slices/modal-slice'
import { SetupActions, useSetupSelectors } from 'web/redux/slices/setup-slice'
import { useAppDispatch } from 'web/redux/store'
import { assertDefined, expectOneById, joinLines, lastItem, tryGetOneById } from 'web/utils/utils'

export const HEADER_CONTAINER_DOM_ID = "global-header-container"

export const HeaderContainer: FC = () => {

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Misc hooks
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    const catchError = useCatchError()
    const isShiftDown = useShiftKey()

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // State
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    const appState = useAppState()
    const headerDisabled = appState.renderMode !== RenderMode.Display
    const inEditRosterView = appState.renderMode === RenderMode.EditRoster

    const displayState = useDisplayState()
    const { selectedFightId, selectedSetupId } = displayState

    const fightState = useFightState()
    const { fights } = fightState

    const fightSelectors = useFightSelectors()
    const { getFullFight } = fightSelectors

    const setupSelectors = useSetupSelectors()
    const { getSetupsForFight } = setupSelectors
    const setups = getSetupsForFight(selectedFightId)

    const buildData = getCurrentBuild()

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Handlers
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    const disp = useAppDispatch()

    const handleManageRoster = () => {
        disp(AppActions.setRenderMode({ renderMode: RenderMode.EditRoster }))
    }

    const handleCreateNewFight = () => {
        disp(ModalActions.openCreateOutlineModal({
            onConfirm: (categoryId, outlineName) => {
                const createdResult = disp(FightActions.createFight({
                    name: outlineName ?? "Untitled",
                    fightId: v4(),
                    categoryId
                }))

                createdResult.then((res) => {
                    if (isFulfilled(res)) {
                        const { fullFight } = res.payload

                        disp(DisplayFightActions.selectFight({ fightId: fullFight.fight.id }))
                        disp(EditFightActions.beginEdit({ fullFight: fullFight, setupFightEvents: [] }))
                        disp(AppActions.setRenderMode({ renderMode: RenderMode.EditFight }))
                    }

                    // TODO: Can I please have a more ergonomic way to
                    // reach the error context?
                    if (isRejected(res)) {
                        catchError(() => { throw res.error })()
                    }
                })
            }
        }))
    }

    const handleCloneFight = () => {
        disp(ModalActions.openPromptModal({
            title: "Enter a new name",
            defaultText: `[CLONE] ${getFullFight(selectedFightId).fight.name}`,
            checkboxes: setups.length >= 1
                ? [
                    { value: "clone-setups", label: `Clone setups too (${setups.length})` },
                ]
                : undefined,
            onConfirm: (text, checked) => {

                const clonedData = cloneFight({
                    fullFight: getFullFight(selectedFightId),
                    setups: (checked?.includes("clone-setups") ? setups : [])
                })

                const { clonedFullFight, clonedSetups } = clonedData

                clonedFullFight.fight.name = text ?? "Untitled"

                disp(FightActions.addNewFight({
                    ...clonedFullFight
                }))

                clonedSetups.forEach(setup => {
                    disp(SetupActions.addNewSetup({ setup }))
                })

                disp(DisplayFightActions.selectFight({ fightId: clonedFullFight.fight.id }))
            }
        }))
    }

    const handleDeleteFight = () => {
        if (fights.length <= 1) {
            disp(ModalActions.openInfoModal({
                title: "Delete fight",
                content: joinLines([
                    "You can't delete the last fight for technical reasons."
                ]),
            }))

            return
        }

        const doDelete = () => {
            disp(FightActions.deleteFight({ fightId: selectedFightId }))

            const remainingFights = fights.filter(f => f.id !== selectedFightId)
            disp(DisplayFightActions.selectFight({ fightId: lastItem(remainingFights).id }))
        }

        if (setups.length <= 0 && getFullFight(selectedFightId).events.length <= 1) {
            doDelete()
            return
        }

        disp(ModalActions.openConfirmModal({
            title: "Delete fight?",
            content: (
                <div>
                    <p>
                        {joinLines([
                            "Deleting a fight will delete all of its associated setups",
                            "as well."
                        ])}
                    </p>

                    <p className="mb-0">
                        {"Are you sure you want to delete this fight?"}
                    </p>
                </div>
            ),
            btnColor: "danger",
            onConfirm: () => {
                disp(SetupActions.deleteSetupsForFight({ fightId: selectedFightId }))
                doDelete()
            }
        }))
    }

    const handlePruneOutlines = () => {
        disp(ModalActions.openPruneOutlinesModal({
            allFights: fights,
            legacyFights: fightSelectors.getLegacyFights()
        }))
    }

    const handleEditFight = () => {

        disp(EditFightActions.beginEdit({
            fullFight: getFullFight(selectedFightId),
            setupFightEvents: getSetupsForFight(selectedFightId).flatMap(setup => setup.setupFightEvents)
        }))

        disp(AppActions.setRenderMode({ renderMode: RenderMode.EditFight }))
    }

    const handleFightSelected = (fightId: string) => {
        disp(DisplayFightActions.selectFight({ fightId }))
    }

    const beginSetupEdit = (mode: 'create' | 'edit') => {
        if (mode === 'create') {
            disp(EditSetupActions.beginEdit({
                fightId: selectedFightId,
                setup: {
                    id: v4(),
                    fightId: selectedFightId,
                    name: "Untitled setup",
                    fightPlayers: [],
                    spellAssignments: [],
                    teamAssignments: [],
                    setupFightEvents: [],

                    patchInfo: buildData.patchInfo,

                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                }
            }))

            disp(EditFightActions.beginEdit({
                fullFight: getFullFight(selectedFightId),
                setupFightEvents: []
            }))

        } else {
            assertDefined(selectedSetupId)
            const selectedSetup = expectOneById(setups, selectedSetupId)

            disp(EditSetupActions.beginEdit({
                fightId: selectedFightId,
                setup: selectedSetup
            }))

            disp(EditFightActions.beginEdit({
                fullFight: getFullFight(selectedFightId, selectedSetup.setupFightEvents),
                setupFightEvents: []
            }))
        }


        disp(AppActions.setRenderMode({ renderMode: RenderMode.EditAssignments }))
    }

    const handleCreateNewSetup = () => {
        beginSetupEdit('create')
    }

    const handleEditSetup = () => {
        beginSetupEdit('edit')
    }

    const handleCloneSetup = () => {
        assertDefined(selectedSetupId)
        const selectedSetup = expectOneById(setups, selectedSetupId)

        disp(ModalActions.openPromptModal({
            title: "Enter a new name",
            defaultText: `[Copy] ${selectedSetup.name}`,

            onConfirm: (text) => {
                const { clonedSetup } = cloneSetup({ setup: selectedSetup })
                clonedSetup.name = text ?? `[Copy] ${selectedSetup.name}`

                disp(SetupActions.addNewSetup({ setup: clonedSetup }))
                disp(DisplayFightActions.selectSetup({ selectedSetupId: clonedSetup.id }))
            }
        }))
    }

    const handleSetupSelected = (setupId: string) => {
        disp(DisplayFightActions.selectSetup({ selectedSetupId: setupId }))
    }

    const handleDeleteSetup = () => {
        assertDefined(selectedSetupId)

        const doDelete = () => {
            disp(DisplayFightActions.clearSelectedSetup())
            disp(SetupActions.deleteSetup({ setupId: selectedSetupId }))
        }

        if (isShiftDown) {
            doDelete()
            return
        }

        disp(ModalActions.openConfirmModal({
            title: "Delete setup?",
            content: (
                <div>
                    <p className="mb-0">
                        {joinLines([
                            "Confirm that you want to delete this setup and all of its",
                            "assignments. This action cannot be undone."
                        ])}
                    </p>
                </div>
            ),
            btnColor: "danger",
            onConfirm: doDelete
        }))
    }

    const handleExport = catchError(() => {

        /*
            TODO: Clean this mess up so it's readable?
        */

        const getMaybeSetup = () => {
            try {
                assertDefined(selectedSetupId)
                return expectOneById(setups, selectedSetupId)
            } catch {
                return undefined
            }
        }

        const fullFightWithoutSetupEvents = getFullFight(
            selectedFightId
        )
        const fullFight = getFullFight(
            selectedFightId,
            tryGetOneById(setups, selectedSetupId ?? 'guaranteed-miss')?.setupFightEvents
        )

        const getSetup = () => {
            assertDefined(selectedSetupId)
            return expectOneById(setups, selectedSetupId)
        }

        disp(ModalActions.openTabbedExportModal({
            title: "Export",
            plaintext: { fullFight: fullFightWithoutSetupEvents },
            share: {
                fightAndSetupString: getMaybeSetup()
                    ? createExportString({
                        setup: getMaybeSetup(),
                        fullFight: fullFightWithoutSetupEvents,
                    })
                    : undefined,

                fightOnlyString: createExportString({
                    fullFight: fullFightWithoutSetupEvents
                })
            },

            // TODO: Allow me to generate a note without a setup, should just
            // list all events
            note: getMaybeSetup() == null
                ? undefined
                : {
                    generateNoteString: (params) => {
                        const exportString = generateSetupExportLines({
                            data: {
                                fullFight,
                                setup: getSetup(),

                            },
                            opt: { ...params }
                        }).join(`\n`)

                        return exportString
                    },

                    generateWaArString: () => {
                        return generateWaArNote({
                            fullFight,
                            setup: getSetup()
                        })
                    }
                }

        }))
    })

    const handleImport = () => {
        disp(ModalActions.openImportFightModal())
    }

    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
    // Render
    // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

    if (inEditRosterView) {
        return (
            <div
                className="py-3"
            >
                <FaButton
                    size="sm"
                    color="primary"
                    icon={faChevronLeft}
                    onClick={catchError(() => {
                        disp(AppActions.setRenderMode({
                            renderMode: RenderMode.Display
                        }))
                    })}
                >
                    {"Back"}
                </FaButton>
            </div>
        )
    }

    return (
        <div
            id={HEADER_CONTAINER_DOM_ID}
            style={{
                opacity: (headerDisabled) ? 0.2 : 1,
                pointerEvents: (headerDisabled) ? 'none' : 'all',
            }}
        >
            <Header
                disableHeader={headerDisabled}

                fightData={{
                    fights: fightState.fights,
                    selectedFightId,
                    legacyFights: fightSelectors.getLegacyFights()
                }}

                fightHandlers={{
                    onCreateNew: handleCreateNewFight,
                    onEdit: handleEditFight,
                    onClone: handleCloneFight,
                    onDelete: handleDeleteFight,
                    onPrune: handlePruneOutlines,

                    onImport: handleImport,
                    onExport: handleExport,

                    onFightSelected: handleFightSelected,
                }}

                setupData={{
                    setups: setups,
                    selectedSetupId
                }}

                setupsHandlers={{
                    onCreateNew: handleCreateNewSetup,
                    onEdit: handleEditSetup,
                    onClone: handleCloneSetup,
                    onDelete: handleDeleteSetup,
                    onSetupSelected: catchError(handleSetupSelected),
                    onManageRoster: handleManageRoster,
                }}
            />
        </div>
    )
}
